import { createSelector } from "@reduxjs/toolkit";

import { selectProjectInvitations } from "Reducers/access/invitation/invitation.selectors";
import { RootState } from "Store/configureStore";

import { normalizeProjectAccessInvitation } from "./normalizeInput";

interface SelectorProps {
  projectId: string;
}

const userAccessesSelector = (state: RootState) => state.userAccess;
const selectProjectId = (_: RootState, { projectId }: SelectorProps) =>
  projectId;

export const userAccessStateByProjectIdSelector = createSelector(
  userAccessesSelector,
  selectProjectId,
  (state, projectId) => state.byProjectId[projectId]
);

export const userAccessDataByProjectIdSelector = createSelector(
  userAccessStateByProjectIdSelector,
  state => state?.data
);

export const userAccessIsLoadingByProjectIdSelector = createSelector(
  userAccessStateByProjectIdSelector,
  state => state?.isLoading
);

export const userAccessAndInvitationsByProjectIdSelector = createSelector(
  userAccessDataByProjectIdSelector,
  selectProjectInvitations,
  (access, invitations) => {
    if (!access || !invitations) return [];
    return [...access, ...invitations.map(normalizeProjectAccessInvitation)];
  }
);

type SortedUserAcess = {
  projectId: string;
  direction: "asc" | "desc";
  key: "email" | "name";
};

export const sortedUserAccessAndInvitationsByProjectIdSelector = createSelector(
  (state: RootState, props: SortedUserAcess) =>
    userAccessAndInvitationsByProjectIdSelector(state, props),
  (_: RootState, props: SortedUserAcess) => props,
  (data, { key, direction }) =>
    data.sort((a, b) => {
      if (!key) return 0;

      const aValue = a[key];
      const bValue = b[key];

      if (aValue < bValue) return direction === "asc" ? -1 : 1;

      if (aValue > bValue) return direction === "asc" ? 1 : -1;

      return 0;
    })
);

export const userAccessCountByProjectIdSelector = createSelector(
  userAccessAndInvitationsByProjectIdSelector,
  accesses => accesses.length
);

export const emailsWithUserAccessByProjectIdSelector = createSelector(
  userAccessDataByProjectIdSelector,
  state => state?.map(({ email }) => email) || []
);

export const userAccessStateByUserIdSelector = createSelector(
  userAccessesSelector,
  (_: RootState, { userId }: { userId: string }) => userId,
  (state, userId) => state.byUserId[userId]
);
